import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useMemo,
} from "react";
import { Drawer } from "@bloomberg/media-ui-article";
import { createClasses, OutlinedButton } from "@bloomberg/media-ui-core";

import { COPY_TEXT, TOPIC_GROUPS } from "../constants";
import TagPicker from "../TagPicker";
import { Topic } from "../types";
import { getFollowedTopicIdsMap } from "../utils";

import styles from "./drawerTogglePicker.module.css";

type UseDrawerTogglePickerProps = {
  showDrawerTopicsPanel: boolean;
  setShowDrawerTopicsPanel: React.Dispatch<React.SetStateAction<boolean>>;
};

type DrawerPickerProps = {
  topics: Topic[];
  unfollowTopicCallback: (topicId: string) => void;
  followTopicCallback: (topicId: string) => void;
  drawerTogglePickerHook: UseDrawerTogglePickerProps;
  onClose: () => void;
  isSignedIn: boolean;
};

// c+p from https://github.com/uidotdev/usehooks/blob/main/index.js
function useClickAway(cb: (_event: TouchEvent | MouseEvent) => void) {
  const ref = useRef<null | HTMLDivElement>(null);
  const refCb = useRef(cb);

  useLayoutEffect(() => {
    refCb.current = cb;
  }, [cb]);

  useEffect(() => {
    const handler = (e: TouchEvent | MouseEvent) => {
      if (!ref.current || !e.target) return;
      const element = ref.current;

      if (element && !element.contains(e.target as Node)) {
        refCb.current(e);
      }
    };

    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  return ref;
}

export const useDrawerTogglePicker = (
  initialShowTopicsPanelState = false
): UseDrawerTogglePickerProps => {
  const [showDrawerTopicsPanel, setShowDrawerTopicsPanel] = useState<boolean>(
    initialShowTopicsPanelState
  );
  const hook = useMemo(() => {
    return { showDrawerTopicsPanel, setShowDrawerTopicsPanel };
  }, [showDrawerTopicsPanel, setShowDrawerTopicsPanel]);

  return hook;
};

export const DrawerTogglePicker = ({
  drawerTogglePickerHook,
  topics,
  followTopicCallback,
  unfollowTopicCallback,
  onClose,
  isSignedIn,
}: DrawerPickerProps) => {
  const [shouldShowDoneButton, setShouldShowDoneButton] =
    useState<boolean>(false);
  const closeShowTopicsPanel = useCallback(() => {
    drawerTogglePickerHook.setShowDrawerTopicsPanel(false);
    onClose();
  }, [drawerTogglePickerHook]);

  const onSelectCallback = useCallback(
    (id: string | number) => {
      setShouldShowDoneButton(true);
      followTopicCallback(id.toString());
    },
    [followTopicCallback]
  );

  const onUnselectCallback = useCallback(
    (id: string | number) => {
      setShouldShowDoneButton(true);
      unfollowTopicCallback(id.toString());
    },
    [setShouldShowDoneButton, unfollowTopicCallback]
  );

  useEffect(() => {
    // The expected behavior is to show the done button if the user has selected a topic.
    // To achieve this we should hide the done button if the drawer closes
    if (!drawerTogglePickerHook.showDrawerTopicsPanel) {
      setShouldShowDoneButton(false);
    }
  }, [drawerTogglePickerHook]);

  const clickAwayDrawerRef = useClickAway(closeShowTopicsPanel);

  return (
    <Drawer open={drawerTogglePickerHook.showDrawerTopicsPanel}>
      <div
        className={createClasses([
          styles.togglePickerDrawerContainer,
          {
            [styles.togglePickerDrawerContainerMaxHeight]:
              !shouldShowDoneButton,
          },
        ])}
        ref={clickAwayDrawerRef}
      >
        <TagPicker
          onClose={closeShowTopicsPanel}
          onUnselect={onUnselectCallback}
          onSelect={onSelectCallback}
          selected={getFollowedTopicIdsMap(topics)}
          label="Follow Topics"
          description={COPY_TEXT}
          itemGroups={TOPIC_GROUPS}
          isSignedIn={isSignedIn}
        />
      </div>
      {shouldShowDoneButton ? (
        <div className={styles.togglePickerFooter}>
          <OutlinedButton
            className={styles.doneButton}
            colorMode="dark"
            size="default"
            onClick={closeShowTopicsPanel}
          >
            Done
          </OutlinedButton>
        </div>
      ) : null}
    </Drawer>
  );
};
